import { Location } from '@angular/common'
import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Router } from '@angular/router'
import { KayttajanTiedot, TositteenLisaysprosessinOsanTunniste } from '@lemon/core-firebase/model/kayttaja'
import { Maksukortti } from '@lemon/core-firebase/model/maksukortti'
import { SelvityspyynnonKirjausrivinPuoli } from '@lemon/core-firebase/model/selvitettavat'
import { FirestoreTosite, FirestoreTositteenAlkuperainenTiedosto, Maksutapa, TositeAiPyynto, TositeAiVastaus, UUDEN_KUITIN_AVAIN } from '@lemon/core-firebase/model/tosite'
import { WebMaksukorttiLisaaPyynto, WebMaksukorttiLisaaVastaus } from '@lemon/core-firebase/model/web-maksukortti-api'
import { WebSelvityspyyntoTallennaPyynto, WebSelvityspyyntoTallennaVastaus, WebTositeTallennaPyynto, WebTositeTallennaVastaus } from '@lemon/core-firebase/model/web-tosite-api'
import { ToimittajaTunnisteService } from '@lemon/core-firebase/service/lasku/toimittaja-tunniste.service'
import { LocalDate } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { addBreadcrumb, captureException } from '@sentry/browser'
import { StorageError } from 'firebase/storage'
import { BehaviorSubject, combineLatest, firstValueFrom, fromEvent, Observable, of, Subject } from 'rxjs'
import { buffer, catchError, debounceTime, filter, first, map, switchMap, takeUntil } from 'rxjs/operators'
import { FirebaseLemonaid } from '../../_angular/service/firebase-lemonaid.service'
import { KayttajaService } from '../../_angular/service/kayttaja.service'
import { TositeDatasourceUusiService } from '../../_angular/service/tosite/tosite-datasource-uusi.service'
import { TositeService } from '../../_angular/service/tosite/tosite.service'
import { AreYouSureDialog, AreYouSureDialogData } from '../../_jaettu-angular/_components/are-you-sure.dialog'
import { FormValidators } from '../../_jaettu-angular/_validators/FormValidators'
import { FileDropEntry } from '../../_jaettu-angular/file-drop/file-drop-entry'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { LocalStorageService } from '../../_jaettu-angular/service/local-storage.service'
import { LahetystenHiljaisuusvahti, SimplifiedUploadData, TiedostojenLataamisService, TiedostonLahetysKeskeytettyError } from '../../_jaettu-angular/service/tiedostojen-lataamis.service'
import { TimestampService } from '../../_jaettu-angular/service/timestamp-service'
import { TositeKuvaCacheService } from '../kuvat/tosite-kuva-cache.service'
import { esivalitseMaksutapaKortilla, etsiEhdotettavaKortti, muotoileKortinNumero, TallennetunTositteenKorttiehdokas } from '../maksukortit/maksukortti-logiikka'
import { MaksukorttiTunnistettuDialog, MaksukorttiTunnistettuDialogData, MaksukorttiTunnistettuDialogTulos } from '../maksukortit/maksukortti-tunnistettu.dialog'
import { MaksukorttiService } from '../maksukortit/maksukortti.service'
import { luoUusiToimittajaLomake, toimittajaValittuValidator, UusiToimittajaLomake, uusiToimittajaLomakkeesta } from './toimittaja-kentta/toimittaja-kentta.component'
import { annaTositteenTallennusestoObservable, muodostaTallennuksenEstojenYhteenveto, onkoTallennusestoja, TallennuksenEstojenYhteenveto, TositteenTallennusesto } from './tositteen-tallennusesto'
import { simuloiVirhetila } from './virhetilojen-simulointi'
import { kutsuTositteenTallennusta } from '../tositteen-tallennuskutsu'

const MAKSUKORTTI_POPUP_VAIMENNETUT_KORTIT_AVAIN = 'maksukorttiPopupVaimennetutKortit'
// Sama raja kuin lueTositteenTiedotAi-callablessa
const AI_LUVUN_MAKSIMI_SIVUT = 15
// Samat päätteet kuin lueTositteenTiedotAi-callablessa, joka hylkää koko pyynnön muun tyyppisestä sivusta
const AI_LUVUN_TUETUT_TIEDOSTOPAATTEET = new Set(['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'pdf'])
const LAHETYKSEN_HILJAISUUSRAJA_MS = 30_000
const YHTEYSTESTIN_AIKARAJA_MS = 10_000
// Firebase Storage lähettää tätä pienemmät tiedostot yhdellä pyynnöllä ilman väli-ilmoituksia etenemisestä
const YHDEN_PYYNNON_LAHETYKSEN_MAKSIMIKOKO = 256 * 1024

type SentryVaihe = 'lahetys' | 'yhteystesti' | 'ai-luku'

interface YhteystestinVirhe {
  vaihe: 'selain-offline' | 'kirjautumistoken' | 'tallennuspalvelu'
  aikaraja: boolean
  virhe: unknown
}

interface JumittunutLahetys {
  virhe: StorageError | Error
  tiedostotyyppi: string
  lahetys: Record<string, unknown>
}

class AikarajaYlittyiError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AikarajaYlittyiError'
  }
}

async function aikarajalla<T>(lupaus: Promise<T>, aikarajaMs: number): Promise<T> {
  let ajastin: ReturnType<typeof setTimeout> | undefined
  const aikaraja = new Promise<never>((_, reject) => {
    ajastin = setTimeout(() => reject(new AikarajaYlittyiError(`Aikaraja ${aikarajaMs} ms ylittyi`)), aikarajaMs)
  })
  try {
    return await Promise.race([lupaus, aikaraja])
  } finally {
    clearTimeout(ajastin)
  }
}

export interface TositeSivu {
  alkuperainen: FirestoreTositteenAlkuperainenTiedosto
  uploadData: SimplifiedUploadData
  tiedosto: File
  esikatselukuva: string
  kuvaUrlit: string[]
  purkuvirhe: boolean
}

export interface TositteenLomake {
  maksutapa: FormControl<number | null>
  paivamaara: FormControl<Date>
  summa: FormControl<number>
  toimittajanNimi: FormControl<string>
  toimittajaAvain: FormControl<string | null>
  uusiToimittaja: FormGroup<UusiToimittajaLomake>
  projektiAvain: FormControl<string>
  lisatiedot: FormControl<string>
  alvVahennysoikeus: FormControl<number | null>
}

export type TositteenLukuTila = 'ei-aloitettu' | 'luetaan' | 'taydennetaan' | 'valmis' | 'epaonnistui' | 'lukukelvoton' | 'keskeytetty'
type KaynnissaOlevaLukuTila = Extract<TositteenLukuTila, 'luetaan' | 'taydennetaan'>

export interface TositeKortinTiedot {
  id: string
  tosite: FirestoreTosite
  formGroup: FormGroup<TositteenLomake>
  sivutSubject: BehaviorSubject<TositeSivu[]>
  uploadKokonaisprosenttiObservable: Observable<number>
  lukuTilaSubject: BehaviorSubject<TositteenLukuTila>
  ohitettuSubject: BehaviorSubject<boolean>
  /** Kasvaa jokaisella AI-luvulla; vanhemman kierroksen tulos hylätään, jos uusi sivu on ehtinyt käynnistää uuden luvun. */
  lukukierros: number
  paivamaaraEsitaytetty: boolean
  muuValuutta: boolean
  uusiToimittajaTekoalylta: boolean
  /** AI:n lukema tunniste rekisteristä löytyneelle toimittajalle; tallennus täydentää sillä toimittajan, jolta tunniste puuttuu. */
  toimittajanTunniste: string | null
  /** Viimeisimmän AI-luvun poiminta-avain; kulkee tallennuspyyntöön luvun tilastointia varten. */
  aiPoimintaAvain: string | null
  tunnistettuKortti: string | null
  /** Tähän korttiin yhdistettyjen korttien AI-lukujen poiminta-avaimet; tallennus poistaa niiden tilastorivit. */
  poistettavatPoimintaAvaimet: string[]
  aiLuku: Promise<void>
  sivujenKasittelyt: Promise<void>[]
}

export interface SelvitettavanTositteenTiedot {
  selvityspyynnonAvain: string
  maksutapa: number | null
  summa: number | null
  kirjausrivinPuoli: SelvityspyynnonKirjausrivinPuoli | null
  pvml: LocalDate | null
}

@Component({
  templateUrl: './lisaa-tositteita.component.html',
  styleUrls: ['./lisaa-tositteita.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LisaaTositteitaComponent implements OnDestroy, OnInit {
  private _ngUnsubscribe = new Subject<void>()

  private _tositteetSubject = new BehaviorSubject<TositeKortinTiedot[]>([])
  private _jumittuneetLahetyksetSubject = new Subject<JumittunutLahetys>()
  private _hiljaisuusvahti = new LahetystenHiljaisuusvahti(LAHETYKSEN_HILJAISUUSRAJA_MS)
  private _selvitettava: SelvitettavanTositteenTiedot | null = null
  private _maksukortit: Maksukortti[] = []
  private _maksutavat: Maksutapa[] = []
  private _kayttajanTiedot: KayttajanTiedot | null = null
  private _sivuAvattu = Date.now()
  readonly naytaVirheet = signal(false)
  readonly kuvavirheAvain = signal('')
  readonly kuvavirheParametrit = signal<Object>({})
  readonly tallennusvirheAvain = signal('')
  readonly yhteysvirheAvain = signal('')

  tositteetObservable: Observable<TositeKortinTiedot[]>
  tositteidenMaaraObservable: Observable<number>
  tallennettavienMaaraObservable: Observable<number>
  kaikkiValmiinaObservable: Observable<boolean>
  tallennuksenEstotObservable: Observable<TallennuksenEstojenYhteenveto>
  alvPortaatObservable: Observable<number[] | null>

  get selvitettavaMoodi(): boolean {
    return this._selvitettava !== null
  }

  constructor(
    private _location: Location,
    private _router: Router,
    private _tositeService: TositeService,
    private _kayttajaService: KayttajaService,
    private _ladataanService: LadataanService,
    private _dateService: DateService,
    private _timestampService: TimestampService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
    private _errorHandler: ErrorHandler,
    private _tositeKuvaCacheService: TositeKuvaCacheService,
    private _currencyService: CurrencyService,
    private _translationService: LemonTranslationService,
    private _tositeDatasourceUusiService: TositeDatasourceUusiService,
    private _toimittajaTunnisteService: ToimittajaTunnisteService,
    private _maksukorttiService: MaksukorttiService,
    private _localStorageService: LocalStorageService,
    private _dialog: MatDialog,
    private _snackbar: MatSnackBar,
  ) {
    this._firebaseLemonaid = simuloiVirhetila(this._firebaseLemonaid)
  }

  ngOnInit() {
    this.tositteetObservable = this._tositteetSubject.asObservable()

    this.tositteidenMaaraObservable = this.tositteetObservable.pipe(map(tositteet => tositteet.length))

    const tallennettavatObservable = this._tositteetSubject.pipe(
      switchMap(tositteet => {
        if (tositteet.length === 0) {
          return of<TositeKortinTiedot[]>([])
        }
        return combineLatest(tositteet.map(t => t.ohitettuSubject)).pipe(map(ohitetut => tositteet.filter((_, i) => !ohitetut[i])))
      }),
    )

    this.tallennettavienMaaraObservable = tallennettavatObservable.pipe(map(tositteet => tositteet.length))

    const tallennusestotObservable = tallennettavatObservable.pipe(
      switchMap(tositteet => (tositteet.length === 0 ? of<TositteenTallennusesto[]>([]) : combineLatest(tositteet.map(annaTositteenTallennusestoObservable)))),
    )

    this.kaikkiValmiinaObservable = tallennusestotObservable.pipe(map(estot => estot.length > 0 && !estot.some(onkoTallennusestoja)))
    this.tallennuksenEstotObservable = tallennusestotObservable.pipe(map(muodostaTallennuksenEstojenYhteenveto))

    const alvPortaatJaOletusObservable = this._kayttajaService.kayttajaObservable.pipe(
      map(kayttaja => {
        if (kayttaja && kayttaja.alvVahennysoikeusPortaat && kayttaja.prosessinOsat) {
          const prosessissaAlv = kayttaja.prosessinOsat.filter(a => a.tunniste === TositteenLisaysprosessinOsanTunniste.ALV_VAHENNYSOIKEUS).length > 0
          if (prosessissaAlv) {
            const portaat: number[] = []
            let oletusProsentti: number | null = null

            for (const porras of kayttaja.alvVahennysoikeusPortaat) {
              if (!isNaN(porras.arvo)) {
                if (porras.oletus) {
                  oletusProsentti = porras.arvo
                }
                portaat.push(porras.arvo)
              }
            }

            portaat.sort((a, b) => a - b)

            return portaat.length > 0 ? { portaat, oletusProsentti } : null
          }
        }
        return null
      }),
    )

    this.alvPortaatObservable = alvPortaatJaOletusObservable.pipe(map(portaatJaOletus => portaatJaOletus?.portaat ?? null))

    combineLatest([alvPortaatJaOletusObservable, this._tositteetSubject])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe({
        next: ([portaatJaOletus, tositteet]) => this._asetaAlvOletusprosentit(portaatJaOletus?.oletusProsentti ?? null, tositteet),
        error: error => this._errorHandler.handleError(error),
      })

    this._maksukorttiService
      .annaMaksukortitObservable()
      .pipe(
        catchError(err => {
          console.error('Maksukorttien haku epäonnistui', err)
          return of<Maksukortti[]>([])
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(kortit => (this._maksukortit = kortit))

    this._tositeService.maksutavatObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(maksutavat => (this._maksutavat = maksutavat))

    // Yhteinen hiljaisuusraja katkaisee kaikki käynnissä olevat lähetykset kerralla, joten niistä raportoidaan yksi tapahtuma.
    this._jumittuneetLahetyksetSubject
      .pipe(buffer(this._jumittuneetLahetyksetSubject.pipe(debounceTime(500))), takeUntil(this._ngUnsubscribe))
      .subscribe(jumittuneet => this._raportoiJumittuneetLahetykset(jumittuneet))

    // Mobiiliselaimet ja lepotila pysäyttävät verkon, kun sivu ei ole näkyvissä, joten se aika ei ole jumia.
    if (document.hidden) {
      this._hiljaisuusvahti.pysayta()
    }
    fromEvent(document, 'visibilitychange')
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => (document.hidden ? this._hiljaisuusvahti.pysayta() : this._hiljaisuusvahti.jatka()))

    this._kasitteleSelvitettavaMoodi()
    this._kasitteleMaksutavallePudotetut()
  }

  private async _kasitteleSelvitettavaMoodi() {
    const tila = this._router.currentNavigation()?.extras.state ?? window.history.state
    const selvitysTiedostot: File[] | undefined = tila?.selvitysTiedostot
    if (selvitysTiedostot && selvitysTiedostot.length > 0 && tila?.selvitettava) {
      this._selvitettava = tila.selvitettava
      await this.tiedostotValittu(selvitysTiedostot)
    }
  }

  private async _kasitteleMaksutavallePudotetut() {
    const tila = this._router.currentNavigation()?.extras.state ?? window.history.state
    const pudotetutTiedostot: File[] | undefined = tila?.pudotetutTiedostot
    const pakotettuMaksutapa: number | undefined = tila?.pakotettuMaksutapa
    if (pudotetutTiedostot && pudotetutTiedostot.length > 0 && pakotettuMaksutapa) {
      await this.tiedostotValittu(pudotetutTiedostot, pakotettuMaksutapa)
    }
  }

  private _esitaytaJaLukitseSelvitettavanKentat(tositteenTiedot: TositeKortinTiedot) {
    if (!this._selvitettava) {
      return
    }
    const pvm = this._selvitettava.pvml ? this._dateService.localDateToDate(this._selvitettava.pvml) : null
    const kontrollit = tositteenTiedot.formGroup.controls
    // Maksutapa tulee aina kirjanpitäjän kirjaukselta eikä sitä näytetä lomakkeella, joten kontrolli on aina disabloitu.
    kontrollit.maksutapa.setValue(this._selvitettava.maksutapa)
    kontrollit.maksutapa.disable()
    if (this._selvitettava.summa !== null) {
      // Selvitettävien tilin kreditrivi on rahan palautus, joten siihen vastaava tosite on hyvitys
      const summa = this._selvitettava.kirjausrivinPuoli === 'kredit' ? -Math.abs(this._selvitettava.summa) : this._selvitettava.summa
      kontrollit.summa.setValue(summa)
      kontrollit.summa.disable()
    }
    if (pvm) {
      kontrollit.paivamaara.setValue(pvm)
      kontrollit.paivamaara.disable()
    }
  }

  ngOnDestroy() {
    this._peruLahetykset(this._tositteetSubject.value.flatMap(t => t.sivutSubject.value))
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  async sulje() {
    if (this._tositteetSubject.value.length > 0) {
      const data: AreYouSureDialogData = {
        header: this._translationService.lokalisoi('tositteet.lisaa-tositteita.sivu.poistu-otsikko'),
        text: this._translationService.lokalisoi('tositteet.lisaa-tositteita.sivu.poistu-teksti'),
        rightAction: this._translationService.lokalisoi('tositteet.lisaa-tositteita.sivu.poistu-vahvista'),
      }
      const sure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
      if (!sure) {
        return
      }
    }
    this._location.back()
  }

  async tiedostotValittu(tiedostot: File[], pakotettuMaksutapa: number | null = null) {
    const sallitut = this._tiedostotSallitut(tiedostot)
    if (!sallitut) {
      return
    }

    const kayttajanTiedot = await this._kayttajaService.getKayttajanTiedot()
    this._kayttajanTiedot = kayttajanTiedot
    this.yhteysvirheAvain.set('')
    this.naytaVirheet.set(false)

    const uudet: TositeKortinTiedot[] = []
    for (const tiedosto of tiedostot) {
      const tositteenTiedot = this._luoUusiTosite(tiedosto, kayttajanTiedot)
      this._esitaytaJaLukitseSelvitettavanKentat(tositteenTiedot)
      if (pakotettuMaksutapa !== null) {
        // Disabloitua kontrollia eivät muuta maksukortin tunnistus eikä yhdistämisen uudelleenluku, joten pudotuskohdan maksutapa säilyy.
        tositteenTiedot.formGroup.controls.maksutapa.setValue(pakotettuMaksutapa)
        tositteenTiedot.formGroup.controls.maksutapa.disable()
      }
      uudet.push(tositteenTiedot)
      const ekaSivu = tositteenTiedot.sivutSubject.value[0]
      tositteenTiedot.sivujenKasittelyt.push(this._kasitteleSivunTiedosto(tositteenTiedot, ekaSivu, tiedosto, kayttajanTiedot))
      this._kaynnistaAiLuku(tositteenTiedot, 'luetaan', false)
    }

    this._tositteetSubject.next([...this._tositteetSubject.value, ...uudet])
  }

  private async _testaaTallennuspalvelunYhteys(): Promise<YhteystestinVirhe | null> {
    if (!navigator.onLine) {
      return { vaihe: 'selain-offline', aikaraja: false, virhe: new Error('Selain ilmoittaa olevansa ilman verkkoyhteyttä') }
    }

    try {
      const kayttaja = this._firebaseLemonaid.auth.currentUser
      if (!kayttaja) {
        throw new Error('Kirjautunutta käyttäjää ei löytynyt')
      }
      await aikarajalla(this._firebaseLemonaid.authGetIdToken(kayttaja), YHTEYSTESTIN_AIKARAJA_MS)
    } catch (err) {
      return { vaihe: 'kirjautumistoken', aikaraja: err instanceof AikarajaYlittyiError, virhe: err }
    }

    try {
      // Listaus vastaa 200 myös tyhjälle kansiolle, joten selaimen konsoliin ei jää virheriviä toisin kuin olemattoman tiedoston haussa.
      await aikarajalla(this._firebaseLemonaid.storageList(`${this._kayttajanTiedot?.asiakasId}`, { maxResults: 1 }), YHTEYSTESTIN_AIKARAJA_MS)
    } catch (err) {
      if (!this._vastasikoTallennuspalvelu(err)) {
        return { vaihe: 'tallennuspalvelu', aikaraja: err instanceof AikarajaYlittyiError, virhe: err }
      }
    }
    return null
  }

  /** Mikä tahansa palvelun vastaus, myös "ei oikeuksia", todistaa yhteyden toimivan. */
  private _vastasikoTallennuspalvelu(err: unknown): boolean {
    if (!(err instanceof StorageError)) {
      return false
    }
    return err.code === 'storage/object-not-found' || err.code === 'storage/unauthorized' || err.status > 0
  }

  private _peruLahetykset(sivut: TositeSivu[]) {
    for (const sivu of sivut) {
      sivu.uploadData.keskeyta?.('peruttu')
    }
  }

  private _raportoiLahetyksenLopputulos(uploadData: SimplifiedUploadData, tiedosto: File) {
    const aloitettu = Date.now()
    const tiedostonTiedot = {
      tiedostotyyppi: tiedosto.type || this._tiedostojenLataamisService.getFileEndingFromFile(tiedosto),
      koko: tiedosto.size,
      lahetystapa: tiedosto.size > YHDEN_PYYNNON_LAHETYKSEN_MAKSIMIKOKO ? 'paloissa' : 'yksi-pyynto',
    }
    addBreadcrumb({ category: 'tosite-lahetys', message: 'Lähetys aloitettu', data: tiedostonTiedot })

    uploadData.statusObservable
      .pipe(
        first(status => status.done),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(status => {
        const kestoMs = Date.now() - aloitettu
        if (!status.error) {
          addBreadcrumb({ category: 'tosite-lahetys', message: 'Lähetys valmis', data: { ...tiedostonTiedot, kestoMs } })
          return
        }

        const virhekoodi = this._annaVirhekoodi(status.error)
        const lahetys = {
          ...tiedostonTiedot,
          virhekoodi,
          httpTila: status.error instanceof StorageError ? status.error.status : null,
          siirretytTavut: Math.round((tiedosto.size * status.donePercentage) / 100),
          kestoMs,
          sivuAukiMs: Date.now() - this._sivuAvattu,
          selainVerkossa: navigator.onLine,
        }
        addBreadcrumb({ category: 'tosite-lahetys', level: 'warning', message: status.syy === 'peruttu' ? 'Lähetys peruttu' : 'Lähetys epäonnistui', data: lahetys })
        if (status.syy === 'peruttu') {
          return
        }
        if (status.syy === 'jumissa') {
          this.yhteysvirheAvain.set('tositteet.lisaa-tositteita.sivu.yhteys-epaonnistui')
          this._jumittuneetLahetyksetSubject.next({ virhe: status.error, tiedostotyyppi: tiedostonTiedot.tiedostotyyppi, lahetys })
          return
        }
        this._raportoiSentryyn(status.error, 'lahetys', virhekoodi, { tiedostotyyppi: tiedostonTiedot.tiedostotyyppi }, { lahetys })
      })
  }

  /** Yhteys testataan vasta jumiutumisen jälkeen ja vain raportointia varten, jotta hidas testi ei koskaan katkaise toimivaa lähetystä eikä viivytä ilmoitusta. */
  private async _raportoiJumittuneetLahetykset(jumittuneet: JumittunutLahetys[]) {
    if (jumittuneet.length === 0) {
      return
    }
    const tiedostotyypit = [...new Set(jumittuneet.map(j => j.tiedostotyyppi))].join(', ')
    const kontekstit = { jumittuneet: { maara: jumittuneet.length, lahetykset: jumittuneet.map(j => j.lahetys) } }

    let yhteysvirhe: YhteystestinVirhe | null = null
    try {
      yhteysvirhe = await this._testaaTallennuspalvelunYhteys()
    } catch (err) {
      console.error('Tallennuspalvelun yhteystesti epäonnistui odottamattomasti', err)
    }
    if (!yhteysvirhe) {
      this._raportoiSentryyn(jumittuneet[0].virhe, 'lahetys', 'jumissa', { tiedostotyyppi: tiedostotyypit }, kontekstit)
      return
    }

    const yhteystesti = {
      vaihe: yhteysvirhe.vaihe,
      aikaraja: yhteysvirhe.aikaraja,
      sivuAukiMs: Date.now() - this._sivuAvattu,
      selainVerkossa: navigator.onLine,
    }
    addBreadcrumb({ category: 'tosite-lahetys', level: 'warning', message: 'Yhteystesti epäonnistui', data: yhteystesti })
    // Offline-tilassa Sentry-tapahtuma ei lähtisi kuitenkaan.
    if (yhteysvirhe.vaihe === 'selain-offline') {
      return
    }
    const virhekoodi = `${yhteysvirhe.vaihe}/${yhteysvirhe.aikaraja ? 'aikaraja' : this._annaVirhekoodi(yhteysvirhe.virhe)}`
    this._raportoiSentryyn(yhteysvirhe.virhe, 'yhteystesti', virhekoodi, { tiedostotyyppi: tiedostotyypit }, { ...kontekstit, yhteystesti })
  }

  private _annaVirhekoodi(virhe: unknown): string {
    if (virhe instanceof TiedostonLahetysKeskeytettyError) {
      return virhe.syy
    }
    const koodi = (virhe as { code?: unknown } | null)?.code
    return typeof koodi === 'string' ? koodi : 'tuntematon'
  }

  /**
   * Raportoidaan suoraan eikä ErrorHandlerin kautta, koska se ei välitä tapahtumakohtaisia tageja.
   * Tagit annetaan kutsukohtaisesti setTagin sijaan, jotta ne eivät vuoda sovelluksen muihin tapahtumiin.
   */
  private _raportoiSentryyn(virhe: unknown, vaihe: SentryVaihe, virhekoodi: string, tagit: Record<string, string>, kontekstit: Record<string, Record<string, unknown>>) {
    captureException(virhe, {
      tags: {
        vaihe,
        virhekoodi,
        asiakasId: this._kayttajanTiedot?.asiakasId,
        asiakasAvain: this._kayttajanTiedot?.asiakasAvain,
        ...tagit,
      },
      contexts: kontekstit,
      fingerprint: ['tosite-lahetys', vaihe, virhekoodi],
    })
  }

  keskeytaLuku(id: string) {
    const tosite = this._etsiTosite(id)
    if (tosite && this._onkoLukuKaynnissa(tosite)) {
      tosite.lukuTilaSubject.next('keskeytetty')
    }
  }

  private _onkoLukuKaynnissa(tositteenTiedot: TositeKortinTiedot): boolean {
    const tila = tositteenTiedot.lukuTilaSubject.value
    return tila === 'luetaan' || tila === 'taydennetaan'
  }

  /** Ketjuttaa luvun kortin aiLukuun, jotta yhdistäminen odottaa myös uudelleenluvun ohittamat kierrokset. */
  private _kaynnistaAiLuku(tositteenTiedot: TositeKortinTiedot, tila: KaynnissaOlevaLukuTila, korvaaAutomaattiset: boolean) {
    const luku = this._lueTositteenTiedotAi(tositteenTiedot, tila, korvaaAutomaattiset)
    tositteenTiedot.aiLuku = Promise.all([tositteenTiedot.aiLuku, luku]).then(() => {})
  }

  private async _lueTositteenTiedotAi(tositteenTiedot: TositeKortinTiedot, tila: KaynnissaOlevaLukuTila, korvaaAutomaattiset: boolean) {
    const omaKierros = ++tositteenTiedot.lukukierros
    const onkoUusinKierros = () => tositteenTiedot.lukukierros === omaKierros && this._onkoLukuKaynnissa(tositteenTiedot)

    tositteenTiedot.lukuTilaSubject.next(tila)
    try {
      const sivut = tositteenTiedot.sivutSubject.value.filter(sivu => AI_LUVUN_TUETUT_TIEDOSTOPAATTEET.has(sivu.alkuperainen.fileEnding?.toLowerCase())).slice(0, AI_LUVUN_MAKSIMI_SIVUT)
      if (sivut.length === 0) {
        tositteenTiedot.lukuTilaSubject.next('epaonnistui')
        return
      }
      const statukset = await firstValueFrom(
        combineLatest(sivut.map(sivu => sivu.uploadData.statusObservable)).pipe(
          filter(tilat => tilat.every(t => t.done)),
          first(),
        ),
      )
      if (!onkoUusinKierros()) {
        return
      }
      const ladatutSivut = sivut.filter((_, i) => !statukset[i].error)
      if (ladatutSivut.length === 0) {
        tositteenTiedot.lukuTilaSubject.next('epaonnistui')
        return
      }

      const pyynto: TositeAiPyynto = {
        kuvakansio: tositteenTiedot.tosite.kuvakansio,
        kuvat: ladatutSivut.map(sivu => ({
          sivunAvain: sivu.alkuperainen.avain,
          tiedostopaate: sivu.alkuperainen.fileEnding,
          ...(sivu.alkuperainen.kuvakansio !== tositteenTiedot.tosite.kuvakansio ? { kuvakansio: sivu.alkuperainen.kuvakansio } : {}),
        })),
      }
      const vastaus = await this._firebaseLemonaid.functionsCall<TositeAiPyynto, TositeAiVastaus>('lueTositteenTiedotAi', pyynto, undefined, 'europe-north1')
      if (tositteenTiedot.lukukierros !== omaKierros) {
        if (vastaus.poimintaAvain) {
          tositteenTiedot.poistettavatPoimintaAvaimet.push(vastaus.poimintaAvain)
        }
        return
      }
      if (tositteenTiedot.aiPoimintaAvain) {
        tositteenTiedot.poistettavatPoimintaAvaimet.push(tositteenTiedot.aiPoimintaAvain)
      }
      tositteenTiedot.aiPoimintaAvain = vastaus.poimintaAvain ?? null

      if (!this._onkoLukuKaynnissa(tositteenTiedot)) {
        return
      }
      if (vastaus.e) {
        console.error(`Tositteen ${tositteenTiedot.id} AI-luku epäonnistui: ${vastaus.e}`)
        addBreadcrumb({ category: 'tosite-ai-luku', level: 'warning', message: 'AI-luku palautti virheen', data: { virhekoodi: vastaus.e } })
        tositteenTiedot.lukuTilaSubject.next(vastaus.e === 'unreadable-image' ? 'lukukelvoton' : 'epaonnistui')
        return
      }

      this._esitaytaAiTiedoilla(tositteenTiedot, vastaus, korvaaAutomaattiset)
      tositteenTiedot.lukuTilaSubject.next('valmis')
    } catch (err) {
      console.error('Tositteen AI-luku epäonnistui', err)
      const sivut = tositteenTiedot.sivutSubject.value
      const aiLuku = { sivumaara: sivut.length, tiedostotyypit: [...new Set(sivut.map(sivu => sivu.alkuperainen.fileEnding))].join(', ') }
      this._raportoiSentryyn(err, 'ai-luku', this._annaVirhekoodi(err), {}, { aiLuku })
      if (onkoUusinKierros()) {
        tositteenTiedot.lukuTilaSubject.next('epaonnistui')
      }
    }
  }

  /**
   * Täyttää vain kentät, joihin käyttäjä ei ole koskenut; selvitettävän lukitut kentät jätetään rauhaan.
   * Yhdistämisen jälkeen korvataan myös aiemman luvun täyttämät arvot, koska uusi luku näkee kaikki sivut.
   */
  private _esitaytaAiTiedoilla(tositteenTiedot: TositeKortinTiedot, vastaus: TositeAiVastaus, korvaaAutomaattiset: boolean) {
    const kontrollit = tositteenTiedot.formGroup.controls

    if (vastaus.paivamaara && (korvaaAutomaattiset || !tositteenTiedot.paivamaaraEsitaytetty) && !kontrollit.paivamaara.dirty && kontrollit.paivamaara.enabled) {
      const [vuosi, kuukausi, paiva] = vastaus.paivamaara.split('-').map(Number)
      const pvm = this._dateService.localDateToDate({ year: vuosi, month: kuukausi, day: paiva })
      if (pvm) {
        kontrollit.paivamaara.setValue(pvm)
        tositteenTiedot.paivamaaraEsitaytetty = true
      }
    }

    // Lomake on euromääräinen, joten muun valuutan summaa ei esitäytetä. Yhdistämisen jälkeen aiemman luvun summa tyhjennetään,
    // koska se on luettu vain osasta sivuja ja tallentuisi muuten varoituksesta huolimatta.
    const euroissa = !vastaus.valuutta || vastaus.valuutta === 'EUR'
    const summaTaytettavissa = korvaaAutomaattiset ? !kontrollit.summa.dirty : !kontrollit.summa.value
    if (kontrollit.summa.enabled) {
      tositteenTiedot.muuValuutta = !euroissa
      if (euroissa && vastaus.summa !== null && summaTaytettavissa) {
        kontrollit.summa.setValue(vastaus.summa)
      } else if (!euroissa && korvaaAutomaattiset && summaTaytettavissa) {
        kontrollit.summa.setValue(null)
      }
    }

    const uusiToimittaja = kontrollit.uusiToimittaja
    const aiLoysiToimittajan = !!(vastaus.toimittajaAvain ? vastaus.toimittaja : vastaus.toimittaja || vastaus.businessId)
    if (korvaaAutomaattiset && aiLoysiToimittajan && !kontrollit.toimittajanNimi.dirty && !kontrollit.toimittajaAvain.dirty && !uusiToimittaja.dirty) {
      kontrollit.toimittajanNimi.setValue('')
      kontrollit.toimittajaAvain.setValue(null)
      uusiToimittaja.reset({ nimi: '', ytunnus: '' }, { emitEvent: false })
      uusiToimittaja.disable()
      tositteenTiedot.toimittajanTunniste = null
      tositteenTiedot.uusiToimittajaTekoalylta = false
    }
    if (!kontrollit.toimittajanNimi.value && !kontrollit.toimittajaAvain.value && uusiToimittaja.disabled) {
      if (vastaus.toimittajaAvain) {
        if (vastaus.toimittaja) {
          kontrollit.toimittajanNimi.setValue(vastaus.toimittaja)
          kontrollit.toimittajaAvain.setValue(vastaus.toimittajaAvain)
          tositteenTiedot.toimittajanTunniste = vastaus.businessId
        }
      } else if (vastaus.toimittaja || vastaus.businessId) {
        kontrollit.toimittajanNimi.setValue(vastaus.toimittaja ?? '')
        uusiToimittaja.enable({ emitEvent: false })
        tositteenTiedot.uusiToimittajaTekoalylta = true
        uusiToimittaja.setValue({ nimi: vastaus.toimittaja ?? '', ytunnus: this._toimittajaTunnisteService.naytettavaTunnisteArvosta(vastaus.businessId) ?? '' })
      }
    }
    tositteenTiedot.tunnistettuKortti = korvaaAutomaattiset ? (vastaus.maksukorttiViimeiset4 ?? null) : (tositteenTiedot.tunnistettuKortti ?? vastaus.maksukorttiViimeiset4 ?? null)
    const aiempiMaksutapa = kontrollit.maksutapa.value
    const korvataankoMaksutapa = korvaaAutomaattiset && !kontrollit.maksutapa.dirty && kontrollit.maksutapa.enabled && aiempiMaksutapa !== null
    if (korvataankoMaksutapa) {
      kontrollit.maksutapa.setValue(null)
    }
    const maksutapaValittiin = esivalitseMaksutapaKortilla(kontrollit.maksutapa, tositteenTiedot.tunnistettuKortti, this._maksukortit, this._maksutavat)
    if (korvataankoMaksutapa && !maksutapaValittiin) {
      kontrollit.maksutapa.setValue(aiempiMaksutapa)
    }
  }

  async lisaaSivuja(id: string, tiedostot: File[]) {
    const sallitut = this._tiedostotSallitut(tiedostot)
    if (!sallitut) {
      return
    }
    const tosite = this._etsiTosite(id)
    if (!tosite) {
      return
    }

    const kayttajanTiedot = await this._kayttajaService.getKayttajanTiedot()
    this._kayttajanTiedot = kayttajanTiedot
    this.yhteysvirheAvain.set('')
    const luodut: { sivu: TositeSivu; tiedosto: File }[] = []

    for (const tiedosto of tiedostot) {
      const sivu = this._luoTositteenSivu(tosite.tosite, tiedosto, kayttajanTiedot)
      luodut.push({ sivu, tiedosto })
    }

    const aiemmatSivut = tosite.sivutSubject.value
    tosite.sivutSubject.next([...aiemmatSivut, ...luodut.map(l => l.sivu)])

    for (const { sivu, tiedosto } of luodut) {
      tosite.sivujenKasittelyt.push(this._kasitteleSivunTiedosto(tosite, sivu, tiedosto, kayttajanTiedot))
    }
    this._kaynnistaUudelleenluku(tosite)
  }

  private _kaynnistaUudelleenluku(tosite: TositeKortinTiedot) {
    this._kaynnistaAiLuku(tosite, tosite.lukuTilaSubject.value === 'luetaan' ? 'luetaan' : 'taydennetaan', false)
  }

  async yritaLahetysUudelleen(id: string) {
    const tosite = this._etsiTosite(id)
    if (!tosite) {
      return
    }
    const kayttajanTiedot = await this._kayttajaService.getKayttajanTiedot()
    this._kayttajanTiedot = kayttajanTiedot
    this.yhteysvirheAvain.set('')
    tosite.ohitettuSubject.next(false)

    const nykyisetSivut = tosite.sivutSubject.value
    const statukset = await firstValueFrom(combineLatest(nykyisetSivut.map(sivu => sivu.uploadData.statusObservable)))
    const uudelleenLahetettavat: TositeSivu[] = []
    const sivut = nykyisetSivut.map((sivu, i) => {
      if (!statukset[i].error) {
        return sivu
      }
      const uusiSivu: TositeSivu = { ...sivu, uploadData: this._lahetaSivunTiedosto(sivu.alkuperainen, sivu.tiedosto, kayttajanTiedot), purkuvirhe: false }
      uudelleenLahetettavat.push(uusiSivu)
      return uusiSivu
    })
    if (uudelleenLahetettavat.length === 0) {
      return
    }
    tosite.sivutSubject.next(sivut)

    for (const sivu of uudelleenLahetettavat) {
      tosite.sivujenKasittelyt.push(this._kasitteleSivunTiedosto(tosite, sivu, sivu.tiedosto, kayttajanTiedot))
    }
    if (uudelleenLahetettavat.length === sivut.length) {
      this._kaynnistaAiLuku(tosite, 'luetaan', false)
    } else {
      this._kaynnistaUudelleenluku(tosite)
    }
  }

  async poistaEpaonnistuneetSivut(id: string) {
    const tosite = this._etsiTosite(id)
    if (!tosite) {
      return
    }
    const sivut = tosite.sivutSubject.value
    const statukset = await firstValueFrom(combineLatest(sivut.map(sivu => sivu.uploadData.statusObservable)))
    for (let i = sivut.length - 1; i >= 0; i--) {
      if (statukset[i].error) {
        this.poistaSivu(id, i)
      }
    }
  }

  ohitaLisays(id: string) {
    this._etsiTosite(id)?.ohitettuSubject.next(true)
  }

  poistaTosite(id: string) {
    const poistettava = this._etsiTosite(id)
    if (poistettava) {
      this._peruLahetykset(poistettava.sivutSubject.value)
    }
    const uudetTositteet = this._tositteetSubject.value.filter(t => t.id !== id)
    this._tositteetSubject.next(uudetTositteet)
  }

  poistaSivu(id: string, sivuIndeksi: number) {
    const tosite = this._etsiTosite(id)
    if (!tosite) {
      return
    }
    const nykyiset = tosite.sivutSubject.value
    if (nykyiset.length <= 1) {
      return
    }
    const poistettava = nykyiset[sivuIndeksi]
    if (!poistettava) {
      return
    }

    this._peruLahetykset([poistettava])

    const poistettavaAvain = poistettava.alkuperainen.avain
    delete tosite.tosite.alkuperaiset[poistettavaAvain]
    for (const kuva of Object.values(tosite.tosite.kuvat)) {
      if (kuva.alkuperaisenAvain === poistettavaAvain) {
        delete tosite.tosite.kuvat[kuva.avain]
      }
    }

    const uudet = nykyiset.filter((_, i) => i !== sivuIndeksi)
    tosite.sivutSubject.next(uudet)

    if (this._onkoLukuKaynnissa(tosite)) {
      this._kaynnistaUudelleenluku(tosite)
    }
  }

  async yhdistaTositteet() {
    const tositteet = this._tositteetSubject.value.filter(t => !t.ohitettuSubject.value)
    if (tositteet.length < 2) {
      return
    }
    const data: AreYouSureDialogData = {
      header: this._translationService.lokalisoi('tositteet.lisaa-tositteita.sivu.yhdista-otsikko'),
      text: this._translationService.lokalisoi('tositteet.lisaa-tositteita.sivu.yhdista-teksti', { maara: tositteet.length }),
      rightAction: this._translationService.lokalisoi('tositteet.lisaa-tositteita.sivu.yhdista-vahvista'),
    }
    const sure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
    if (!sure) {
      return
    }

    try {
      this._ladataanService.aloitaLataaminen()

      // Kesken olevat luvut kirjoittavat vielä korttiensa kuviin ja poiminta-avaimiin, jotka siirretään kohteelle.
      await Promise.all(tositteet.flatMap(t => [t.aiLuku, ...t.sivujenKasittelyt]))

      // Ohitettuja ei yhdistetä, koska yhdistetty tosite tallentuisi niiden tiedostoineen.
      const nykyiset = this._tositteetSubject.value
      const yhdistettavat = nykyiset.filter(t => tositteet.includes(t) && !t.ohitettuSubject.value)
      if (yhdistettavat.length < 2) {
        return
      }
      const [kohde, ...lahteet] = yhdistettavat
      const kayttajanTiedot = await this._kayttajaService.getKayttajanTiedot()
      const siirretytSivut = lahteet.flatMap(lahde =>
        lahde.sivutSubject.value.map(sivu => ({ ...sivu, kuvaUrlit: sivu.kuvaUrlit.map(url => url.replaceAll(lahde.tosite.kuvakansio, kohde.tosite.kuvakansio)) })),
      )
      this._tositeKuvaCacheService.siirraKuvatKuitille(
        kayttajanTiedot,
        lahteet.map(lahde => lahde.tosite),
        kohde.tosite,
      )
      kohde.poistettavatPoimintaAvaimet = yhdistettavat.flatMap(t => [...t.poistettavatPoimintaAvaimet, ...(t.aiPoimintaAvain ? [t.aiPoimintaAvain] : [])])
      kohde.aiPoimintaAvain = null
      kohde.sivutSubject.next([...kohde.sivutSubject.value, ...siirretytSivut])
      this._tositteetSubject.next(nykyiset.filter(t => !lahteet.includes(t)))
      this._kaynnistaAiLuku(kohde, 'luetaan', true)
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async tallennaTositteet() {
    if (!(await firstValueFrom(this.kaikkiValmiinaObservable))) {
      this._naytaTallennuksenEstot()
      return
    }
    this.naytaVirheet.set(false)
    try {
      this._ladataanService.aloitaLataaminen()
      this.tallennusvirheAvain.set('')

      const tositteet = this._tositteetSubject.value.filter(t => !t.ohitettuSubject.value)
      const tallennettavat: TositeKortinTiedot[] = []

      for (const tositteenTiedot of tositteet) {
        const valmis = await this._valmisteleTallennettavaTosite(tositteenTiedot)
        if (!valmis) {
          return
        }
        tallennettavat.push(tositteenTiedot)
      }

      // Onnistuneet poistetaan näkymästä tallennuksen aikana, joten korttiehdokkaat otetaan talteen ennen sitä.
      const korttiehdokkaat = tallennettavat.map(t => ({ tunnistettuKortti: t.tunnistettuKortti, maksutapa: t.formGroup.controls.maksutapa.value }))

      const kaikkiTallentuivat = this._selvitettava ? await this._tallennaSelvityspyynnonVastaus(this._selvitettava, tallennettavat) : await this._tallennaPalvelimella(tallennettavat)
      if (!kaikkiTallentuivat) {
        return
      }

      const viimeinen = tallennettavat[tallennettavat.length - 1]?.tosite
      if (viimeinen) {
        this._tositeDatasourceUusiService.changeVuosiKk(viimeinen.localPvm.year, viimeinen.localPvm.month - 1)
      }

      if (this.selvitettavaMoodi) {
        await this._router.navigate(['tositteet', 'osto'])
        return
      }

      if (viimeinen) {
        await this._tositeService.setLastViewedKuitinMaksutapa(viimeinen.maksutapa)
      }

      const kohde = this._tositeService.getMaksutapaNameForRouter(viimeinen?.maksutapa)
      await this._router.navigate(['tositteet', kohde])
      this._ehdotaTunnistetunKortinTallennusta(korttiehdokkaat)
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  private _naytaTallennuksenEstot() {
    this.naytaVirheet.set(true)
    for (const tositteenTiedot of this._tositteetSubject.value) {
      tositteenTiedot.formGroup.markAllAsTouched()
    }
    setTimeout(() => {
      const virheellinenKortti = document.querySelector<HTMLElement>('main.page .block.is-error')
      const kohde = virheellinenKortti?.querySelector<HTMLElement>('.mat-form-field-invalid') ?? virheellinenKortti ?? document.querySelector<HTMLElement>('main.page .block.is-odottaa')
      kohde?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 100)
  }

  /** Ajetaan odottamatta navigoinnin jälkeen; mikään virhe ei saa katkaista tallennusflowta. */
  private async _ehdotaTunnistetunKortinTallennusta(ehdokkaat: TallennetunTositteenKorttiehdokas[]) {
    try {
      const vaimennetutKortit = this._localStorageService.getFromLocalStorage<string[]>(MAKSUKORTTI_POPUP_VAIMENNETUT_KORTIT_AVAIN) ?? []
      const ehdotus = etsiEhdotettavaKortti(ehdokkaat, this._maksukortit, this._maksutavat, vaimennetutKortit)
      if (!ehdotus) {
        return
      }

      const data: MaksukorttiTunnistettuDialogData = { viimeiset4: ehdotus.viimeiset4, maksutapa: ehdotus.maksutapa }
      const tulos = await firstValueFrom(
        this._dialog.open<MaksukorttiTunnistettuDialog, MaksukorttiTunnistettuDialogData, MaksukorttiTunnistettuDialogTulos>(MaksukorttiTunnistettuDialog, { data }).afterClosed(),
      )

      if (tulos === 'alaKysy') {
        this._localStorageService.setToLocalStorage(MAKSUKORTTI_POPUP_VAIMENNETUT_KORTIT_AVAIN, [...vaimennetutKortit, ehdotus.viimeiset4])
        return
      }
      if (tulos !== 'yhdista') {
        return
      }

      const kortinNumero = muotoileKortinNumero(ehdotus.viimeiset4)
      try {
        const pyynto: WebMaksukorttiLisaaPyynto = { maksutapaTunniste: ehdotus.maksutapa.i, viimeiset4: ehdotus.viimeiset4 }
        const vastaus = await this._firebaseLemonaid.functionsCall<WebMaksukorttiLisaaPyynto, WebMaksukorttiLisaaVastaus>('maksukorttiLisaaWeb', pyynto, undefined, 'europe-north1')
        if (vastaus.e) {
          throw new Error(`Maksukortin ${kortinNumero} yhdistäminen epäonnistui: ${vastaus.e}`)
        }
        this._snackbar.open(this._translationService.lokalisoi('tositteet.maksukortit.tunnistettu.yhdistetty'), 'OK', { duration: 5000 })
      } catch (err) {
        this._errorHandler.handleError(err)
        this._snackbar.open(this._translationService.lokalisoi('tositteet.maksukortit.tunnistettu.yhdistaminen-epaonnistui'), 'OK', { duration: 10000 })
      }
    } catch (err) {
      console.error('Maksukortin tallennusehdotus epäonnistui', err)
    }
  }

  /**
   * Kortit tallennetaan rinnakkain: palvelin odottaa kuvabinäärejä jopa 30 s korttia kohden,
   * joten peräkkäin ajettuna usean kortin odotukset kasautuisivat saman spinnerin taakse.
   * Saman tallentamattoman toimittajan jakavista korteista lähetetään ensin vain yksi, ja loput
   * saavat sen vastauksesta luodun toimittajan avaimen, jotta toimittaja syntyy rekisteriin vain kerran.
   * Onnistuneet poistetaan näkymästä, joten uusi yritys lähettää vain epäonnistuneet.
   */
  private async _tallennaPalvelimella(tallennettavat: TositeKortinTiedot[]): Promise<boolean> {
    const edeltajat = this._ratkaiseUudenToimittajanEdeltajat(tallennettavat)
    const ensimmaiset = tallennettavat.filter(tositteenTiedot => !edeltajat.has(tositteenTiedot.id))
    const ensimmaistenTulokset = await this._tallennaKortit(ensimmaiset.map(tositteenTiedot => ({ tositteenTiedot, pyynto: this._luoTallennuspyynto(tositteenTiedot) })))

    const luodutAvaimet = new Map(
      ensimmaistenTulokset.flatMap(tulos => (tulos.status === 'fulfilled' && tulos.value.onnistui && tulos.value.toimittajaAvain ? [[tulos.value.id, tulos.value.toimittajaAvain] as const] : [])),
    )
    const seuraajat = tallennettavat
      .filter(tositteenTiedot => edeltajat.has(tositteenTiedot.id))
      .map(tositteenTiedot => {
        const pyynto = this._luoTallennuspyynto(tositteenTiedot)
        const avain = luodutAvaimet.get(edeltajat.get(tositteenTiedot.id))
        if (avain) {
          delete pyynto.uusiToimittaja
          pyynto.toimittajaAvain = avain
        }
        return { tositteenTiedot, pyynto }
      })
    const seuraajienTulokset = seuraajat.length > 0 ? await this._tallennaKortit(seuraajat) : []
    const tulokset = [...ensimmaistenTulokset, ...seuraajienTulokset]

    const onnistuneidenIdt = new Set(tulokset.flatMap(tulos => (tulos.status === 'fulfilled' && tulos.value.onnistui ? [tulos.value.id] : [])))
    this._tositteetSubject.next(this._tositteetSubject.value.filter(t => !onnistuneidenIdt.has(t.id)))

    const ensimmainenPoikkeus = tulokset.find((tulos): tulos is PromiseRejectedResult => tulos.status === 'rejected')
    if (ensimmainenPoikkeus) {
      throw ensimmainenPoikkeus.reason
    }

    if (onnistuneidenIdt.size < tallennettavat.length) {
      this.tallennusvirheAvain.set('tositteet.lisaa-tositteita.sivu.tallennus-epaonnistui')
      return false
    }
    return true
  }

  private _tallennaKortit(kortit: { tositteenTiedot: TositeKortinTiedot; pyynto: WebTositeTallennaPyynto }[]) {
    return Promise.allSettled(
      kortit.map(async ({ tositteenTiedot, pyynto }) => {
        const vastaus = await kutsuTositteenTallennusta<WebTositeTallennaPyynto, WebTositeTallennaVastaus>(this._firebaseLemonaid, 'tositeTallennaWeb', pyynto)
        if (vastaus.e && vastaus.e !== 'already-exists') {
          console.error(`Tositteen ${pyynto.avain} tallennus epäonnistui: ${vastaus.e}`, vastaus.puuttuvat)
          return { id: tositteenTiedot.id, onnistui: false, toimittajaAvain: null }
        }
        return { id: tositteenTiedot.id, onnistui: true, toimittajaAvain: vastaus.toimittajaAvain ?? null }
      }),
    )
  }

  /** Kortit, joilla on sama tallentamaton toimittaja: avaimena kortin id, arvona ryhmän ensimmäisen kortin id. */
  private _ratkaiseUudenToimittajanEdeltajat(tallennettavat: TositeKortinTiedot[]): Map<string, string> {
    const ensimmaisetRyhmittain = new Map<string, string>()
    const edeltajat = new Map<string, string>()
    for (const tositteenTiedot of tallennettavat) {
      const uusiToimittaja = uusiToimittajaLomakkeesta(tositteenTiedot.formGroup.controls.uusiToimittaja)
      if (!uusiToimittaja) {
        continue
      }
      const tunnisteet = [...this._toimittajaTunnisteService.muodostaToimittajanTunnisteet(uusiToimittaja.tunnisteet).tunnisteet].sort().join('|')
      const ryhma = tunnisteet || `nimi:${uusiToimittaja.nimi.toLowerCase()}`
      const ensimmainen = ensimmaisetRyhmittain.get(ryhma)
      if (ensimmainen) {
        edeltajat.set(tositteenTiedot.id, ensimmainen)
      } else {
        ensimmaisetRyhmittain.set(ryhma, tositteenTiedot.id)
      }
    }
    return edeltajat
  }

  private async _tallennaSelvityspyynnonVastaus(selvitettava: SelvitettavanTositteenTiedot, tallennettavat: TositeKortinTiedot[]): Promise<boolean> {
    // Ilman maksutapaa Lemonatorin tositteiden vastaanotto hylkää tositteen hiljaisesti, joten tallennus estetään.
    if (!Number.isFinite(selvitettava.maksutapa) || selvitettava.maksutapa < 1) {
      this.tallennusvirheAvain.set('tositteet.lisaa-tositteita.sivu.maksutapa-puuttuu')
      return false
    }
    const pyynto: WebSelvityspyyntoTallennaPyynto = {
      avain: selvitettava.selvityspyynnonAvain,
      tositteet: tallennettavat.map(tositteenTiedot => this._luoTallennuspyynto(tositteenTiedot)),
    }
    const vastaus = await kutsuTositteenTallennusta<WebSelvityspyyntoTallennaPyynto, WebSelvityspyyntoTallennaVastaus>(this._firebaseLemonaid, 'tositeSelvityspyyntoTallennaWeb', pyynto)
    if (vastaus.e) {
      console.error(`Selvityspyynnön ${pyynto.avain} vastauksen tallennus epäonnistui: ${vastaus.e}`, vastaus.puuttuvat)
      this.tallennusvirheAvain.set(vastaus.e === 'not-found' ? 'tositteet.selvitys.pyynto-poistettu' : 'tositteet.lisaa-tositteita.sivu.tallennus-epaonnistui')
      return false
    }
    this._tositteetSubject.next([])
    return true
  }

  private _luoTallennuspyynto(tositteenTiedot: TositeKortinTiedot): WebTositeTallennaPyynto {
    const kuitti = tositteenTiedot.tosite
    if (kuitti.avain === UUDEN_KUITIN_AVAIN) {
      kuitti.avain = this._firebaseLemonaid.firestoreCreateId()
    }
    const pyynto: WebTositeTallennaPyynto = {
      avain: kuitti.avain,
      uusi: true,
      p: kuitti.p,
      sum: kuitti.sum,
      selite: kuitti.selite ?? '',
      maksutapa: kuitti.maksutapa,
      kuvakansio: kuitti.kuvakansio,
      lisatytKuvat: tositteenTiedot.sivutSubject.value.map((sivu, indeksi) => ({
        avain: sivu.alkuperainen.avain,
        nimi: sivu.alkuperainen.nimi,
        tiedostopaate: sivu.alkuperainen.fileEnding,
        jarjestys: indeksi,
        ...(sivu.alkuperainen.kuvakansio !== kuitti.kuvakansio ? { kuvakansio: sivu.alkuperainen.kuvakansio } : {}),
      })),
      poistetutKuvat: [],
    }
    if (kuitti.seller) {
      pyynto.seller = kuitti.seller
    }
    const uusiToimittaja = uusiToimittajaLomakkeesta(tositteenTiedot.formGroup.controls.uusiToimittaja)
    if (uusiToimittaja) {
      pyynto.uusiToimittaja = uusiToimittaja
    } else if (kuitti.toimittajaAvain) {
      pyynto.toimittajaAvain = kuitti.toimittajaAvain
      if (tositteenTiedot.toimittajanTunniste) {
        pyynto.toimittajanTunniste = tositteenTiedot.toimittajanTunniste
      }
    }
    if (kuitti.projektiAvain) {
      pyynto.projektiAvain = kuitti.projektiAvain
    }
    if (kuitti.alvVahennysoikeus !== null && kuitti.alvVahennysoikeus !== undefined) {
      pyynto.alvVahennysoikeus = kuitti.alvVahennysoikeus
    }
    if (tositteenTiedot.aiPoimintaAvain) {
      pyynto.aiPoimintaAvain = tositteenTiedot.aiPoimintaAvain
    }
    if (tositteenTiedot.poistettavatPoimintaAvaimet.length > 0) {
      pyynto.poistettavatPoimintaAvaimet = tositteenTiedot.poistettavatPoimintaAvaimet
    }
    return pyynto
  }

  private _luoUusiTosite(tiedosto: File, kayttajanTiedot: KayttajanTiedot): TositeKortinTiedot {
    const kuitti = this._tositeService.annaUusiKuitti()

    const sivu = this._luoTositteenSivu(kuitti, tiedosto, kayttajanTiedot)
    const sivutSubject = new BehaviorSubject<TositeSivu[]>([sivu])
    const formGroup = this._luoTositeLomake(kuitti, null)

    return {
      id: this._firebaseLemonaid.firestoreCreateId(),
      tosite: kuitti,
      formGroup: formGroup,
      sivutSubject: sivutSubject,
      uploadKokonaisprosenttiObservable: sivutSubject.pipe(switchMap(sivut => this._tiedostojenLataamisService.annaKokonaisprosenttiSimplified(sivut.map(s => s.uploadData)))),
      lukuTilaSubject: new BehaviorSubject<TositteenLukuTila>('ei-aloitettu'),
      ohitettuSubject: new BehaviorSubject(false),
      lukukierros: 0,
      paivamaaraEsitaytetty: false,
      muuValuutta: false,
      uusiToimittajaTekoalylta: false,
      toimittajanTunniste: null,
      aiPoimintaAvain: null,
      tunnistettuKortti: null,
      poistettavatPoimintaAvaimet: [],
      aiLuku: Promise.resolve(),
      sivujenKasittelyt: [],
    }
  }

  private _luoTositteenSivu(kuitti: FirestoreTosite, tiedosto: File, kayttajanTiedot: KayttajanTiedot): TositeSivu {
    const fileEnding = this._tiedostojenLataamisService.getFileEndingFromFile(tiedosto)

    const alkuperainen: FirestoreTositteenAlkuperainenTiedosto = {
      avain: this._firebaseLemonaid.firestoreCreateId(),
      kuvakansio: kuitti.kuvakansio,
      nimi: tiedosto.name,
      fileEnding: fileEnding,
      kasitelty: false,
    }
    kuitti.alkuperaiset[alkuperainen.avain] = alkuperainen

    return {
      alkuperainen: alkuperainen,
      uploadData: this._lahetaSivunTiedosto(alkuperainen, tiedosto, kayttajanTiedot),
      tiedosto: tiedosto,
      esikatselukuva: '',
      kuvaUrlit: [],
      purkuvirhe: false,
    }
  }

  private _lahetaSivunTiedosto(alkuperainen: FirestoreTositteenAlkuperainenTiedosto, tiedosto: File, kayttajanTiedot: KayttajanTiedot): SimplifiedUploadData {
    const uploadData = this._tiedostojenLataamisService.tallennaTositetiedostoSimplified(
      this._firebaseLemonaid,
      kayttajanTiedot.asiakasId + '',
      alkuperainen,
      tiedosto,
      undefined,
      this._hiljaisuusvahti,
    )
    this._raportoiLahetyksenLopputulos(uploadData, tiedosto)
    return uploadData
  }

  private _luoTositeLomake(kuitti: FirestoreTosite, maksutapaId: number | null): FormGroup<TositteenLomake> {
    const formGroup = new FormGroup<TositteenLomake>(
      {
        maksutapa: new FormControl<number | null>(maksutapaId, [Validators.required]),
        paivamaara: new FormControl<Date>(new Date(), { nonNullable: true, validators: [Validators.required] }),
        summa: new FormControl<number>(null, { validators: [Validators.required, Validators.min(-999999.99), FormValidators.eiNolla(), Validators.max(999999.99)] }),
        toimittajanNimi: new FormControl<string>('', { nonNullable: true }),
        toimittajaAvain: new FormControl<string | null>(null),
        uusiToimittaja: luoUusiToimittajaLomake(this._toimittajaTunnisteService),
        projektiAvain: new FormControl<string>('', { nonNullable: true }),
        lisatiedot: new FormControl<string>('', { nonNullable: true }),
        alvVahennysoikeus: new FormControl<number | null>(kuitti.alvVahennysoikeus ?? null),
      },
      { validators: [toimittajaValittuValidator] },
    )

    formGroup.controls.maksutapa.valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(arvo => {
      kuitti.maksutapa = arvo
    })

    formGroup.controls.paivamaara.valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(arvo => {
      kuitti.pvm = this._timestampService.dateToTimestamp(arvo)
      kuitti.localPvm = this._dateService.dateToLocalDate(arvo)
      kuitti.p = this._dateService.dateToNumber(arvo)
    })

    formGroup.controls.summa.valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(arvo => {
      kuitti.summa = arvo === null || arvo === undefined ? arvo : Math.abs(arvo)
      kuitti.sum = arvo
    })

    formGroup.controls.toimittajanNimi.valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(arvo => {
      kuitti.seller = arvo
    })

    formGroup.controls.toimittajaAvain.valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(arvo => {
      if (arvo) {
        kuitti.toimittajaAvain = arvo
      } else {
        delete kuitti.toimittajaAvain
      }
    })

    formGroup.controls.projektiAvain.valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(arvo => {
      kuitti.projektiAvain = arvo
    })

    formGroup.controls.lisatiedot.valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(arvo => {
      kuitti.selite = arvo
    })

    formGroup.controls.alvVahennysoikeus.valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(arvo => {
      kuitti.alvVahennysoikeus = arvo
    })

    return formGroup
  }

  private async _kasitteleSivunTiedosto(tositteenTiedot: TositeKortinTiedot, sivu: TositeSivu, tiedosto: File, kayttajanTiedot: KayttajanTiedot) {
    this._luoEsikatselukuva(tiedosto, tositteenTiedot, sivu.alkuperainen.avain)

    try {
      const fileDropEntry = new FileDropEntry(tiedosto.name, null)
      let purettu = await this._tositeService.puraYksiTiedostoLokaalisti(
        tositteenTiedot.tosite,
        tiedosto,
        fileDropEntry,
        sivu.alkuperainen.fileEnding,
        sivu.uploadData,
        sivu.alkuperainen,
        kayttajanTiedot,
        false,
        Promise.resolve({}),
      )

      if (purettu.virhe) {
        purettu = await this._tositeService.puraYksiTiedostoBackendissa(tositteenTiedot.tosite, sivu.uploadData, sivu.alkuperainen, kayttajanTiedot, false, Promise.resolve({}))
      }
      if (purettu.virhe) {
        this._merkitseSivunPurkuvirhe(tositteenTiedot, sivu.alkuperainen.avain)
        return
      }

      if (purettu.kasitellytKuvat) {
        const jarjestetytKuvat = [...purettu.kasitellytKuvat].sort((a, b) => a.jarjestys - b.jarjestys)
        for (const kuva of jarjestetytKuvat) {
          tositteenTiedot.tosite.kuvat[kuva.avain] = kuva
        }
        const kuvaUrlit = jarjestetytKuvat.map(k => this._tositeKuvaCacheService.annaKuvanUrl(kayttajanTiedot, tositteenTiedot.tosite, k, 'full'))
        const paivitetytSivut = tositteenTiedot.sivutSubject.value.map(s => (s.alkuperainen.avain === sivu.alkuperainen.avain ? { ...s, kuvaUrlit } : s))
        tositteenTiedot.sivutSubject.next(paivitetytSivut)
      }
    } catch (err) {
      console.error('Tiedoston käsittely epäonnistui:', err)
      this._merkitseSivunPurkuvirhe(tositteenTiedot, sivu.alkuperainen.avain)
    }
  }

  private _merkitseSivunPurkuvirhe(tositteenTiedot: TositeKortinTiedot, sivunAvain: string) {
    const paivitetytSivut = tositteenTiedot.sivutSubject.value.map(s => (s.alkuperainen.avain === sivunAvain ? { ...s, purkuvirhe: true } : s))
    tositteenTiedot.sivutSubject.next(paivitetytSivut)
  }

  private _luoEsikatselukuva(tiedosto: File, tositteenTiedot: TositeKortinTiedot, sivunAvain: string) {
    if (tiedosto.type === 'application/pdf') {
      return
    }
    const lukija = new FileReader()
    lukija.onload = e => {
      if (!e.target?.result) {
        return
      }
      const sivut = tositteenTiedot.sivutSubject.value
      const paivitetytSivut = sivut.map(s => (s.alkuperainen.avain === sivunAvain ? { ...s, esikatselukuva: e.target.result as string } : s))
      tositteenTiedot.sivutSubject.next(paivitetytSivut)
    }
    lukija.readAsDataURL(tiedosto)
  }

  private _etsiTosite(id: string): TositeKortinTiedot | undefined {
    return this._tositteetSubject.value.find(t => t.id === id)
  }

  private _tiedostotSallitut(tiedostot: File[]): boolean {
    this.kuvavirheAvain.set('')
    this.kuvavirheParametrit.set({})

    const supportedImageTypes = ['jpeg', 'jpg', 'png', 'webp', 'tiff', 'gif', 'svg', 'pdf', 'heic', 'heif']
    for (const tiedosto of tiedostot) {
      const fileEnding = this._tiedostojenLataamisService.getFileEndingFromFile(tiedosto)
      if (!fileEnding || supportedImageTypes.indexOf(fileEnding.toLowerCase()) < 0) {
        this.kuvavirheParametrit.set({ tuetutMuodot: supportedImageTypes.join(', ') })
        this.kuvavirheAvain.set('kuitit.lataaminen.virheet.vaara-muoto')
        return false
      }
      if (tiedosto.size === 0) {
        this.kuvavirheParametrit.set({ tiedostonNimi: tiedosto.name })
        this.kuvavirheAvain.set('kuitit.lataaminen.virheet.tyhja')
        return false
      }
      const maxKoko = 25 * 1024 * 1024
      if (tiedosto.size > maxKoko) {
        const kokoMegatavuissaLokalisoitu = this._currencyService.formatoiDesimaali(tiedosto.size / 1024 / 1024, 2, this._translationService.nykyinenKieli)
        const maxKokoLokalisoitu = this._currencyService.formatoiDesimaali(maxKoko / 1024 / 1024, 2, this._translationService.nykyinenKieli)
        this.kuvavirheParametrit.set({ kokoMax: maxKokoLokalisoitu, kokoNyt: kokoMegatavuissaLokalisoitu })
        this.kuvavirheAvain.set('kuitit.lataaminen.virheet.liian-suuri')
        return false
      }
    }
    return true
  }

  private _asetaAlvOletusprosentit(oletusProsentti: number | null, tositteet: TositeKortinTiedot[]) {
    if (oletusProsentti !== null && oletusProsentti !== undefined && tositteet) {
      for (const tositteenTiedot of tositteet) {
        if (tositteenTiedot.tosite.alvVahennysoikeus === null || tositteenTiedot.tosite.alvVahennysoikeus === undefined) {
          tositteenTiedot.tosite.alvVahennysoikeus = oletusProsentti
          tositteenTiedot.formGroup.controls.alvVahennysoikeus.setValue(oletusProsentti)
        }
      }
    }
  }

  private async _valmisteleTallennettavaTosite(tositteenTiedot: TositeKortinTiedot): Promise<FirestoreTosite | null> {
    const kuitti = tositteenTiedot.tosite

    const uploadData = tositteenTiedot.sivutSubject.value.map(s => s.uploadData)
    const virheet = await firstValueFrom(this._tiedostojenLataamisService.annaKaikkiVirheetSimplified(uploadData))
    if (virheet && virheet.length > 0) {
      return null
    }

    const valmis = await firstValueFrom(combineLatest(uploadData.map(ud => ud.statusObservable)).pipe(map(statukset => statukset.every(s => s.done))))
    if (!valmis) {
      return null
    }

    return kuitti
  }
}
